import { UserRole } from '../types';

export interface RecruitmentInsight {
  id: string;
  type: 'pipeline_health' | 'time_to_fill' | 'market_alert' | 'diversity' | 'requisition' | 'offer';
  title: string;
  value: number | string;
  unit?: string;
  change?: number;
  changeType?: 'positive' | 'negative' | 'neutral';
  department?: string;
  region?: string;
  roleFamily?: string;
  timestamp: Date;
  recordCount?: number; // For privacy threshold checking
  drillThroughPath?: string;
  priority?: 'high' | 'medium' | 'low';
  metadata?: Record<string, any>;
}

export interface TeamRequisitionSummary {
  requisitionId: string;
  title: string;
  department: string;
  manager: string;
  status: 'OPEN' | 'ON_HOLD' | 'CLOSED' | 'FILLED';
  daysOpen: number;
  candidateCount: number;
  stageCount: {
    applied: number;
    screening: number;
    interview: number;
    offer: number;
    hired: number;
  };
  nextActions: Array<{
    type: 'interview' | 'review' | 'decision';
    count: number;
    dueDate?: Date;
  }>;
}

export interface PipelineHealthMetrics {
  overallHealth: number; // 0-100 score
  conversionRates: {
    appliedToScreen: number;
    screenToInterview: number;
    interviewToOffer: number;
    offerToHire: number;
  };
  bottlenecks: Array<{
    stage: string;
    severity: 'high' | 'medium' | 'low';
    impact: string;
  }>;
  byDepartment: Record<string, {
    health: number;
    openReqs: number;
    avgTimeToFill: number;
  }>;
}

class RecruitmentInsightsService {
  private static instance: RecruitmentInsightsService;
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private MIN_RECORDS_THRESHOLD = 5; // Privacy threshold

  private constructor() {}

  static getInstance(): RecruitmentInsightsService {
    if (!RecruitmentInsightsService.instance) {
      RecruitmentInsightsService.instance = new RecruitmentInsightsService();
    }
    return RecruitmentInsightsService.instance;
  }

  /**
   * Get recruitment insights for HR Leader dashboard
   */
  async getHRLeaderInsights(
    department?: string,
    region?: string
  ): Promise<RecruitmentInsight[]> {
    const cacheKey = `hr_leader_${department || 'all'}_${region || 'all'}`;
    
    // Check cache
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    // Simulate API call - in production, this would fetch from backend
    const insights: RecruitmentInsight[] = [
      {
        id: 'hl_1',
        type: 'pipeline_health',
        title: 'Pipeline Health Score',
        value: 78,
        unit: '%',
        change: 5,
        changeType: 'positive',
        timestamp: new Date(),
        recordCount: 245,
        drillThroughPath: '/requisitions?view=pipeline',
        priority: 'medium'
      },
      {
        id: 'hl_2',
        type: 'time_to_fill',
        title: 'Avg. Time to Fill',
        value: 42,
        unit: 'days',
        change: -8,
        changeType: 'positive',
        timestamp: new Date(),
        recordCount: 89,
        drillThroughPath: '/requisitions?view=metrics&metric=time_to_fill',
        roleFamily: 'Engineering'
      },
      {
        id: 'hl_3',
        type: 'market_alert',
        title: 'High Difficulty Roles',
        value: 12,
        unit: 'positions',
        change: 3,
        changeType: 'negative',
        timestamp: new Date(),
        recordCount: 12,
        drillThroughPath: '/market-intelligence?filter=high_difficulty',
        priority: 'high'
      },
      {
        id: 'hl_4',
        type: 'diversity',
        title: 'Diversity Pipeline',
        value: 34,
        unit: '%',
        change: 2,
        changeType: 'positive',
        timestamp: new Date(),
        recordCount: 156,
        drillThroughPath: '/requisitions?view=diversity'
      }
    ];

    // Apply department/region filtering if specified
    let filteredInsights = insights;
    if (department) {
      filteredInsights = filteredInsights.map(i => ({ ...i, department }));
    }
    if (region) {
      filteredInsights = filteredInsights.map(i => ({ ...i, region }));
    }

    this.setCached(cacheKey, filteredInsights);
    return filteredInsights;
  }

  /**
   * Get recruitment insights for Manager dashboard
   */
  async getManagerInsights(
    managerId: string,
    department: string
  ): Promise<{
    insights: RecruitmentInsight[];
    requisitions: TeamRequisitionSummary[];
  }> {
    const cacheKey = `manager_${managerId}_${department}`;
    
    // Check cache
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    // Manager-specific insights (filtered to their team only)
    const insights: RecruitmentInsight[] = [
      {
        id: 'mg_1',
        type: 'requisition',
        title: 'Open Positions',
        value: 3,
        unit: 'roles',
        timestamp: new Date(),
        recordCount: 3,
        drillThroughPath: `/requisitions?manager=${managerId}`,
        department
      },
      {
        id: 'mg_2',
        type: 'offer',
        title: 'Pending Offers',
        value: 1,
        unit: 'offer',
        timestamp: new Date(),
        recordCount: 1,
        drillThroughPath: `/requisitions?manager=${managerId}&stage=offer`,
        department
      },
      {
        id: 'mg_3',
        type: 'time_to_fill',
        title: 'Avg. Days Open',
        value: 28,
        unit: 'days',
        change: -3,
        changeType: 'positive',
        timestamp: new Date(),
        recordCount: 3,
        department
      }
    ];

    // Team requisitions summary
    const requisitions: TeamRequisitionSummary[] = [
      {
        requisitionId: 'REQ-2024-089',
        title: 'Senior Software Engineer',
        department,
        manager: managerId,
        status: 'OPEN',
        daysOpen: 15,
        candidateCount: 24,
        stageCount: {
          applied: 24,
          screening: 12,
          interview: 5,
          offer: 1,
          hired: 0
        },
        nextActions: [
          { type: 'interview', count: 2, dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000) },
          { type: 'decision', count: 1, dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000) }
        ]
      },
      {
        requisitionId: 'REQ-2024-093',
        title: 'Product Designer',
        department,
        manager: managerId,
        status: 'OPEN',
        daysOpen: 8,
        candidateCount: 18,
        stageCount: {
          applied: 18,
          screening: 8,
          interview: 2,
          offer: 0,
          hired: 0
        },
        nextActions: [
          { type: 'review', count: 8, dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000) }
        ]
      },
      {
        requisitionId: 'REQ-2024-087',
        title: 'QA Engineer',
        department,
        manager: managerId,
        status: 'FILLED',
        daysOpen: 42,
        candidateCount: 31,
        stageCount: {
          applied: 31,
          screening: 15,
          interview: 8,
          offer: 2,
          hired: 1
        },
        nextActions: []
      }
    ];

    const result = { insights, requisitions };
    this.setCached(cacheKey, result);
    return result;
  }

  /**
   * Get pipeline health metrics with role-based filtering
   */
  async getPipelineHealthMetrics(
    role: UserRole,
    scope?: { department?: string; region?: string; managerId?: string }
  ): Promise<PipelineHealthMetrics> {
    const cacheKey = `pipeline_${role}_${JSON.stringify(scope || {})}`;
    
    // Check cache
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    // Base metrics (would come from backend in production)
    let metrics: PipelineHealthMetrics = {
      overallHealth: 78,
      conversionRates: {
        appliedToScreen: 73,
        screenToInterview: 53,
        interviewToOffer: 29,
        offerToHire: 79
      },
      bottlenecks: [
        {
          stage: 'Interview to Offer',
          severity: 'high',
          impact: 'Low conversion rate affecting time-to-fill'
        },
        {
          stage: 'Screening',
          severity: 'medium',
          impact: 'Backlog of 45 candidates awaiting review'
        }
      ],
      byDepartment: {
        'Engineering': { health: 82, openReqs: 12, avgTimeToFill: 38 },
        'Sales': { health: 75, openReqs: 8, avgTimeToFill: 31 },
        'Marketing': { health: 71, openReqs: 5, avgTimeToFill: 45 },
        'Operations': { health: 79, openReqs: 7, avgTimeToFill: 29 }
      }
    };

    // Apply role-based filtering
    if (role === 'MANAGER' && scope?.department) {
      // Managers only see their department's data
      const deptData = metrics.byDepartment[scope.department];
      metrics = {
        ...metrics,
        byDepartment: deptData ? { [scope.department]: deptData } : {}
      };
    } else if (role === 'RECRUITER' && scope?.managerId) {
      // Recruiters see data for assigned requisitions
      // Filter based on assigned manager relationships
    }

    this.setCached(cacheKey, metrics);
    return metrics;
  }

  /**
   * Check if data meets privacy thresholds
   */
  private meetsPrivacyThreshold(recordCount: number): boolean {
    return recordCount >= this.MIN_RECORDS_THRESHOLD;
  }

  /**
   * Apply privacy masking to sensitive metrics
   */
  applyPrivacyMasking(
    insights: RecruitmentInsight[],
    role: UserRole
  ): RecruitmentInsight[] {
    return insights.map(insight => {
      // Check if insight has enough records
      if (insight.recordCount && !this.meetsPrivacyThreshold(insight.recordCount)) {
        return {
          ...insight,
          value: 'N/A',
          change: undefined,
          metadata: {
            ...insight.metadata,
            masked: true,
            reason: 'insufficient_data'
          }
        };
      }

      // Apply role-specific masking rules
      if (role === 'MANAGER') {
        // Managers don't see certain sensitive metrics
        if (insight.type === 'diversity' || insight.type === 'market_alert') {
          return {
            ...insight,
            value: 'N/A',
            metadata: {
              ...insight.metadata,
              masked: true,
              reason: 'role_restriction'
            }
          };
        }
      }

      return insight;
    });
  }

  /**
   * Subscribe to real-time updates
   */
  subscribeToUpdates(
    role: UserRole,
    scope: any,
    callback: (insights: RecruitmentInsight[]) => void
  ): () => void {
    // In production, this would set up WebSocket or SSE connection
    const intervalId = setInterval(async () => {
      if (role === 'HR_LEADER') {
        const insights = await this.getHRLeaderInsights(scope?.department, scope?.region);
        callback(insights);
      } else if (role === 'MANAGER') {
        const data = await this.getManagerInsights(scope?.managerId, scope?.department);
        callback(data.insights);
      }
    }, 30000); // Update every 30 seconds

    // Return cleanup function
    return () => clearInterval(intervalId);
  }

  /**
   * Cache management
   */
  private getCached(key: string): any {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data;
    }
    return null;
  }

  private setCached(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  clearCache(): void {
    this.cache.clear();
  }
}

export default RecruitmentInsightsService.getInstance();